import type { UseCaseConfig } from "../types";
import { CORE_FIELDS } from "../shared/form-fields";
import { USE_CASE_SEO } from "../seo-manifest";
import { TS_CASE_STUDY_HREF } from "../case-studies/teachingStrategies";
import { CrossLink } from "../../components/landing/CrossLink";
import { TeachingStrategiesCaseStudy } from "../../components/use-cases/TeachingStrategiesCaseStudy";

/**
 * Education / K–12 use-case configuration. Copy preserved verbatim from the
 * original EducationCaseStudy.tsx + subcomponents at the time of the
 * config-driven refactor; any future edits to the education page happen here.
 */
export const educationConfig: UseCaseConfig = {
  slug: "education",
  menuLabel: "Education",
  navLabel: "Use Case · Education",
  teaser:
    "Celebrate the school year for every school, district or program you serve.",
  meta: USE_CASE_SEO.education,
  hero: {
    eyebrowLabel: "Use Case",
    subhead: "Education · K–12",
    headlineLead: "Your school year, ",
    headlineEmphasis: "celebrated.",
    body: "Forward helps schools, districts, and the education platforms that serve them turn data into narratives worth sharing. Outcomes, milestones, and momentum, delivered in a format that gets opened.",
    byline: "Mobile-first. Slideshow ready.",
  },
  narrativeSample: {
    reportId: "mca_year_in_review_20260509_y4r2k7",
    badgeLabel: "Sample",
    sampleTitle: "Sample Year in Review for a fictional school",
    eyebrow: "The format",
    captionHeadline: "On a phone, in your voice.",
    captionBody:
      "Tap or swipe through a sample Year in Review. Each section pairs a single headline number with the moments behind it, sized for a phone screen and shaped to your brand.",
    disclosure: (
      <>
        The sample is a Year in Review for <em>Meridian Collegiate Academy</em>,
        a fictional charter school we built to demonstrate the format. For a
        real campaign, see the{" "}
        <CrossLink href={TS_CASE_STUDY_HREF}>
          Teaching Strategies case study
        </CrossLink>{" "}
        below.
      </>
    ),
    fullScreenLabel: "Open the sample full screen →",
    ctaLabel: "Open sample",
    watermark: "YiR",
  },
  caseStudy: <TeachingStrategiesCaseStudy />,
  scale: {
    eyebrow: "From one school to a national program",
    leadIn: "Whether you’re a ",
    firstHighlight: "single school",
    middle: (
      <>
        {" "}
        with hundreds of students,
        <br className="hidden md:block" /> or a{" "}
      </>
    ),
    secondHighlight: "national program",
    tail: (
      <>
        ,
        <br />
        the same framework scales with you.
      </>
    ),
    subtitle: (
      <>
        Run one recap for one campus. Or generate personalized chapters for
        every school in a network from the same data pipeline. Brand, voice, and
        pacing stay consistent.
      </>
    ),
    counters: ["1 school", "20", "500", "10K", "500K", "National"],
  },
  pillars: {
    eyebrow: "For schools, specifically",
    headline:
      "Three things that matter when the audience is busy and the data is yours.",
    points: [
      {
        title: "Works with what you already track.",
        body: "Enrollment, attendance, assessments, scholarship totals, alumni outcomes. If it lives in a spreadsheet or an SIS export, it can become a section. No new integrations to stand up.",
      },
      {
        title: "Built to be opened, not skimmed.",
        body: "Mobile-first, swipeable, sized for a phone screen. Designed for the moments between meetings, not a board packet.",
      },
      {
        title: "Tells your voice, not ours.",
        body: "Brand colors, fonts, photography, and the music behind each section all reflect your institution. The sample format is a starting template, not a fixed structure.",
      },
    ],
  },
  cta: {
    sectionEyebrow: "Your school",
    headlineLead: "What would ",
    headlineEmphasis: "your",
    headlineTail: " Year in Review look like?",
    body: "Tell us a little about your school. We’ll come back inside of one to two business days with a mocked-up first draft built around the kind of data you already report on.",
    submitLabel: "Send it →",
    sendingLabel: "Sending…",
    continueLabel: "Continue →",
    success: {
      headline: "We’ll be in touch.",
      body: "Someone from our team will reach out within one to two business days with a first draft mocked up for your school.",
    },
    fields: [
      ...CORE_FIELDS,
      {
        key: "audience_takeaway",
        type: "textarea",
        label: "What do you want your audience to take away?",
        placeholder:
          "e.g. A clear sense of momentum. Pride in the work behind this year’s results.",
        required: true,
        core: false,
      },
    ],
  },
};
